<script lang="ts">
  import * as Sentry from "@sentry/browser";
  import { form } from "svelte-forms2";
  import { readable, Readable } from "svelte/store";

  //services
  import {
    blur,
    change,
    selectChange,
    SvelteFormInputEvent,
  } from "@shared/services/svelte_form_input_service";
  import {
    businessDetailsErrorsMap,
    retailerLiquorLicenseTypes,
    setErrors,
    trackErrorImpressions,
    userFriendlyErrors,
  } from "@onboarding2/services/retailer_user_signup";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { extractGooglePlaceData } from "@onboarding2/services/google_places_autocomplete_service";
  import { RetailerEntity } from "@shared/services/event_tracking/entities";

  // stores
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";

  // components
  import FormInputError from "@shared/svelte/Components/FormInputError.svelte";
  import Header from "@onboarding2/svelte/Shared/Header.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Info from "@onboarding2/svelte/Signup/Info.svelte";
  import RetailerSelected from "./RetailerSelected.svelte";
  import SearchInput from "@shared/svelte/Components/SearchInput.svelte";
  import SelectInput from "@shared/svelte/Components/SelectInput.svelte";
  import TextInput from "@shared/svelte/Components/TextInput.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";

  // types
  import type { FormField } from "@shared/types/Form";
  import type { RetailerType } from "@shared/types/Retailer";

  export let fields: RetailerAutocompleteSignupFields;
  export let googlePlaceResult = null;
  export let hasSelectedRetailer: boolean = false;
  export let retailer: RetailerType;
  export let retailerStore;

  type RetailerAutocompleteSignupFields = {
    licenseType: FormField;
    liquorLicense: FormField;
    name: FormField;
    nonAlcoholic: FormField;
  };

  const { trackClick, trackEvent, immediatelyProcessEventQueue } = EventTracking;

  const businessNotSelectedErrorMessage: string = "Select your business to continue";
  const businessDetailsForm = form(...Object.values(fields));
  const liquorLicense = businessDetailsForm.getField("liquor_license");
  const nonAlcoholic = businessDetailsForm.getField("non_alcoholic");
  const params: Readable<any> = readable(window["stateService"].params);

  let businessNotSelectedError: boolean = false;
  let hasSubmitted: boolean = false;
  let submitting: boolean = false;

  const handleAutoCompleteFocus = (_e: CustomEvent): void => {
    trackClick("onboarding_autocomplete_name_field");
  };

  const handleSubmit = (_e: Event): Promise<void> => {
    trackClick("onboarding_continue_button");

    return save(retailer, goNext);
  };

  const goNext = async (): Promise<void> => {
    await immediatelyProcessEventQueue();

    window.location.reload();
  };

  const onBlur = (e: SvelteFormInputEvent) => {
    businessNotSelectedError = !hasSelectedRetailer;
    blur(e);
  };

  const save = async (retailer: RetailerType, successCallback: () => void): Promise<void> => {
    hasSubmitted = true;

    if (!hasSelectedRetailer) {
      return trackErrorImpressions([businessNotSelectedErrorMessage]);
    }

    await businessDetailsForm.validate();

    if (!$businessDetailsForm.valid) {
      return trackErrorImpressions(
        userFriendlyErrors($businessDetailsForm.errors, businessDetailsErrorsMap),
      );
    }

    submitting = true;

    // We're setting accepted terms to true because they agree by clicking the sign-up button
    Object.assign(retailer, {
      ...extractGooglePlaceData(googlePlaceResult),
      ...$businessDetailsForm.summary,
      accepted_terms: true,
      completed_onboarding: true,
    });

    if (retailer.non_alcoholic) {
      retailer.liquor_license = null;
    }

    if (retailer.liquor_license) {
      retailer.liquor_license_ack = true;
      retailer.license_terms_ack_by_user_id = $CurrentUserStore.id;
    } else {
      retailer.liquor_license_ack = false;
      retailer.license_terms_ack_by_user_id = null;
    }

    if (hasSelectedRetailer) {
      retailer.google_place_location_data = googlePlaceResult;
    }

    retailerStore
      .save(retailer)
      .then(res => {
        trackEvent("retailer_created", [
          RetailerEntity.fromRetailer({ ...retailer, id: res.retailer_id }),
        ]);
        successCallback();
      })
      .catch((error: { errors: { messages: string[]; keys: string[] } }) => {
        if (error.errors.messages[0] == "Authentication required") {
          Sentry.captureMessage(
            "Unauthenticated attempt to create a retailer in the Google Places signup flow.",
          );
          setTimeout(() => window.location.reload(), 500);
        }

        setErrors(retailerStore, error.errors.messages);
        submitting = false;
      });
  };

  $: selectedRetailer =
    hasSelectedRetailer && googlePlaceResult ? extractGooglePlaceData(googlePlaceResult) : null;

  $: if ($liquorLicense?.value === "") $liquorLicense.value = null;
</script>

<style>
  #form-container {
    padding: 2rem 1.5rem;
  }

  @media (min-width: 1175px) {
    #form-container {
      padding: 3rem 4rem;
    }
  }
</style>

<div class="fizz-layout-header-50-50" style="min-height: 100vh;">
  <Header
    displayLogInOption={false}
    hideTitleOnMobile={true}
    title={"Bars, restaurants and retailers"}
    isSevenFiftyUser={false}
  />
  <Info />
  <div data-grid-area="main" style="background: #F5F7F7">
    <div class="fizz-surface fizz-flow-16 fizz-radius-medium fizz-elevation-1" id="form-container">
      <h3 class="fizz-heading-3 fizz-text-center fizz-stack-8">Let's get your business set up</h3>
      <p class="fizz-text-center fizz-text-small fizz-stack-32">
        Set up your business to connect with distributors, browse their catalogs, and place orders.
        You can add more locations later.
      </p>
      <div id="biz-name-container">
        <div class:fizz-is-hidden={hasSelectedRetailer}>
          <SearchInput
            field={fields.name}
            id="name"
            name="name"
            on:blur={onBlur}
            placeholder="Find your business (name or DBA)"
            on:change={change}
            on:click={handleAutoCompleteFocus}
          >
            {#if (hasSubmitted && !hasSelectedRetailer) || businessNotSelectedError}
              <p class="fizz-flex-row-4 fizz-text-urgent fizz-text-medium fizz-text-regular">
                <Icon type="Error" height={20} width={16} />
                <span>
                  Select your business or
                  <UIRouterLink
                    stateName="layout.signedIn.detailsManual"
                    onClickCB={() => trackClick("onboarding_add_business_manually_link")}
                    params={$params}
                  >
                    add it manually
                  </UIRouterLink>
                </span>
              </p>
            {/if}
          </SearchInput>
        </div>
        {#if hasSelectedRetailer}
          <RetailerSelected retailer={selectedRetailer} on:unselected />
        {/if}
      </div>
      <form on:submit|preventDefault={handleSubmit} data-testid="retailer-form">
        <div id="premise-container">
          <SelectInput
            options={{
              list: retailerLiquorLicenseTypes,
              textKey: "text",
              valueKey: "value",
            }}
            field={fields.licenseType}
            id="license_type"
            label="Premise Type"
            name="license_type"
            on:change={e => {
              selectChange(e);
              trackClick("onboarding_license_type_field");
            }}
          >
            {#each Object.entries(businessDetailsErrorsMap.license_type) as [key, error]}
              <FormInputError form={businessDetailsForm} errorName="license_type.{key}">
                {error}
              </FormInputError>
            {/each}
          </SelectInput>
        </div>
        <div
          id="liquor-license-container"
          class="fizz-stack-8"
          class:fizz-is-hidden={$nonAlcoholic?.value}
        >
          <TextInput
            classes={"fizz-flex-item fizz-full-width"}
            field={fields.liquorLicense}
            id="liquor_license"
            label="Liquor License"
            labelClasses="fizz-flex-row-8 fizz-stack-8"
            name="liquor_license"
            optional={true}
            on:blur={blur}
            on:change={change}
            on:click={() => trackClick("onboarding_liquor_license_field")}
          />
        </div>
        <p
          class="fizz-flex-row fizz-subdued fizz-text-small fizz-stack-16"
          class:fizz-is-hidden={$nonAlcoholic?.value}
        >
          By adding a liquor license, you certify that it is currently valid and duly licensed by
          the appropriate governmental authority.
        </p>
        <div id="non-alcoholic-container" class="fizz-stack-32">
          <label class="fizz-flex-row-8 fizz-stack-12" for="non_alcoholic">
            <input
              type="checkbox"
              class="fizz-checkbox"
              name="non_alcoholic"
              id="non_alcoholic"
              on:click={() => trackClick("onboarding_non_alcoholic_checkbox")}
              bind:checked={$nonAlcoholic.value}
            />
            <span>My business does not sell alcohol</span>
          </label>
        </div>
        <button
          class="fizz-button-secondary fizz-full-width fizz-button-large fizz-stack-32"
          disabled={submitting}
          type="submit"
        >
          Create your business account
        </button>
      </form>
    </div>
  </div>
</div>
